import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetImageOrderV1WorkspacesWorkspaceIdPdpsPdpIdImageOrderGetData,
  type GetImageOrderV1WorkspacesWorkspaceIdPdpsPdpIdImageOrderGetError,
  type GetImageOrderV1WorkspacesWorkspaceIdPdpsPdpIdImageOrderGetResponse,
  getImageOrderV1WorkspacesWorkspaceIdPdpsPdpIdImageOrderGet,
} from '../../generated';

export type GetImageOrderQueryVariables = GetImageOrderV1WorkspacesWorkspaceIdPdpsPdpIdImageOrderGetData['path'];

export type GetImageOrderQuery = GetImageOrderV1WorkspacesWorkspaceIdPdpsPdpIdImageOrderGetResponse;

export const useGetImageOrderQuery = <
  TData = GetImageOrderQuery,
  TError = GetImageOrderV1WorkspacesWorkspaceIdPdpsPdpIdImageOrderGetError,
>(
  { workspace_id, pdp_id }: GetImageOrderQueryVariables,
  options?: Omit<UseQueryOptions<GetImageOrderQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageOrderQuery, TError, TData>({
    queryKey: ['GetImageOrder', { workspace_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getImageOrderV1WorkspacesWorkspaceIdPdpsPdpIdImageOrderGet({
        path: { workspace_id, pdp_id },
        throwOnError: true,
      });
      return data as GetImageOrderQuery;
    },
    ...options,
  });
};

useGetImageOrderQuery.getKey = ({ workspace_id, pdp_id }: GetImageOrderQueryVariables) => [
  'GetImageOrder',
  { workspace_id, pdp_id },
];

useGetImageOrderQuery.fetcher = (
  { workspace_id, pdp_id }: GetImageOrderQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getImageOrderV1WorkspacesWorkspaceIdPdpsPdpIdImageOrderGet({
      path: { workspace_id, pdp_id },
      throwOnError: true,
    });
    return data as GetImageOrderQuery;
  };
};

useGetImageOrderQuery.schema = {
  method: 'get',
  path: '/v1/workspaces/{workspace_id}/pdps/{pdp_id}/image-order',
};
